import { useState, useContext, useEffect } from "react";
import DeleteModal from "../../common/DeleteModal";
import Tooltip from "../../../components/common/Tooltip";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { v4 as uuid } from "uuid";
import { db, timestamp } from "../../../firebase/config";
import { useHistory } from "react-router-dom";
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { client } from "../../../hooks/Client";
import DragIndicatorOutlinedIcon from "@mui/icons-material/DragIndicatorOutlined";
import Modal from 'react-modal';
import { useFirestoreGeneralOrderBy } from '../../../firebase/useFirestore';
import KeyboardArrowUpOutlinedIcon from '@mui/icons-material/KeyboardArrowUpOutlined';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import Table from "../../../components/common/table/Table.jsx";
import ReadOnlyGuard from "../../common/ReadOnlyGuard";
import softDeleteDoc from "../../../helpers/softDeleteDoc";

const PresenceTable = ({
  item,
  length,
  getStartTimeFromSchedule,
  getEndTimeFromSchedule,
  auth,
  personaId,
  visibleData,
}) => {
  // State
  const [dragArray, setDragArray] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [reportToDelete, setReportToDelete] = useState("");
  const [openModal, setOpenModal] = useState(false);
    const [updatedDocid, setUpdatedDocid] = useState(null);
    const [updatedStart, setUpdatedStart] = useState(null);
    const [updatedEnd, setUpdatedEnd] = useState(null);
    const [orderDates, setOrderDates] = useState('asc');
  const [modalType, setMOdalType] = useState('attendance');
  const [updatedScheduleStart, setUpdatedScheduleStart] = useState(null);
  const [updatedScheduleEnd, setUpdatedScheduleEnd] = useState(null);

  // Firestore
    const presenceReports = useFirestoreGeneralOrderBy(
        'PersonaPresenceReports',
        'PersonaID',
        item.ID ? item.ID : '',
        'Position',
        orderDates,
      );
  
  // Hooks
  const history = useHistory();
  const dayNames = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

  // Modal settings
      Modal.setAppElement('#root');
    
      const modalStyles = {
        content: {
          top: '50%',
          left: '50%',
          right: 'auto',
          bottom: 'auto',
          marginRight: '-50%',
          transform: 'translate(-50%, -50%)',
        },
      };

  // Update dragArray when Firestore data changes
  useEffect(() => {
    setDragArray(presenceReports);
  }, [presenceReports]);

  // DRAG & DROP: reorder items and update Firestore positions
  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(dragArray);
    const [movedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, movedItem);

    // Update local state immediately
    setDragArray(items);

    // Update Firestore with new positions
    items.forEach((item, idx) => {
      db.collection("PersonaPresenceReports")
        .doc(item.docid)
        .update({ Position: idx + 1 });
    });
  };

  // Helper to format a number-of-hours (e.g. 9) into "09:00"
  const formatHourToTimeInput = (hourNum) => {
    const h = String(hourNum).padStart(2, '0');
    return `${h}:00`;
  };

  // Helper to format a Dutch date string to "YYYY-MM-DD" for input fields
  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    // If date is already in the correct format, return it
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;

    try {
      const dutchMonths = {
        januari: "01",
        februari: "02",
        maart: "03",
        april: "04",
        mei: "05",
        juni: "06",
        juli: "07",
        augustus: "08",
        september: "09",
        oktober: "10",
        november: "11",
        december: "12",
      };

      const parts = dateString.split(" ");
      if (parts.length >= 3) {
        const day = parts[1].padStart(2, "0");
        const month = dutchMonths[parts[2].toLowerCase()];
        const year = parts[3];
        if (day && month && year) return `${year}-${month}-${day}`;
      }
      console.warn("Could not parse date:", dateString);
      return "";
    } catch (error) {
      console.error("Error parsing date:", error);
      return "";
    }
  };

  // Helper to get the current weekday info and formatted date
  const getCurrentWeekdayInfo = () => {
    const currentDate = new Date();
    const currentWeekday = dayNames[currentDate.getDay()];
    const formattedDate = currentDate.toLocaleDateString("nl-NL", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    return {
      date: formattedDate,
      type: "correct",
      start: getStartTimeFromSchedule(currentWeekday),
      end: getEndTimeFromSchedule(currentWeekday),
    };
  };

  // Helper to parse hour from a string or number
  const parseHourFromString = (timeStr) => {
    // timeStr might already be a number, or a string "HH:MM"
    if (typeof timeStr === "string" && timeStr.includes(":")) {
      return parseInt(timeStr.split(":")[0], 10);
    }
    // fallback: try to coerce to Number
    return Number(timeStr) || 0;
  };
  
  // Helper to get the starttime from the schedule
  const getScheduledStartHours = () => {
    const today = new Date();
    const dayNamesEn = [
      "sunday","monday","tuesday",
      "wednesday","thursday","friday","saturday"
    ];
    const weekdayKey = dayNamesEn[today.getDay()];

    const rawStart = getStartTimeFromSchedule(weekdayKey);
  
    const start = parseHourFromString(rawStart);
  
    return start;
  };

  // Helper to get the endtime from the schedule
  const getScheduledEndHours = () => {
    const today = new Date();
    const dayNamesEn = [
      "sunday","monday","tuesday",
      "wednesday","thursday","friday","saturday"
    ];
    const weekdayKey = dayNamesEn[today.getDay()];    

    const rawEnd   = getEndTimeFromSchedule(weekdayKey);
    const end   = parseHourFromString(rawEnd);

    return end ;
  };

  // Helper to compute minutes of presence, using corrected times if present
  const getMinutesBetweenTimes = (
    rawStart, rawEnd,
    correctedStart, correctedEnd,
    type
  ) => {
    // if they marked it absent, zero minutes
    if (type === "absent") return 0;

    const start = correctedStart || rawStart;
    const end   = correctedEnd   || rawEnd;

    return convertTimeToMinutes(end) - convertTimeToMinutes(start);
  };

  // Helper to pick corrected times if set, else fall back to raw
  const getCorrectedMinutes = presence => {
    const start = presence.CorrectedStart || presence.Start;
    const end   = presence.CorrectedEnd   || presence.End;
    return convertTimeToMinutes(end) - convertTimeToMinutes(start);
  };


  // Add a new presence report document to Firestore
  const addReport = () => {
    const { date, type, start, end } = getCurrentWeekdayInfo();

    db.collection("PersonaPresenceReports")
      .doc()
      .set({
        Date: date,
        Type: type,
        Start: start || 9,
        End: end || 17,
        Schedule: getScheduledEndHours() - getScheduledStartHours(),
        CorrectedStart: "",
        CorrectedEnd: "",
        Absent: "",
        Reason: "",
        PresenceID: "",
        PersonaID: personaId,
        CompagnyID: client,
        Timestamp: timestamp,
        Author: auth.ID,
        ID: uuid(),
        Position: length + 1,
      });
  };

  // Update the date of a presence report document
  const updatePresenceDate = (e) => {
    const newDate = e.target.value;
    const formattedDate = new Date(newDate).toLocaleDateString("nl-NL", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    const docid = e.target.dataset.docid;

    db.collection("PersonaPresenceReports")
      .doc(docid)
      .update({
        Date: formattedDate,
      })
      .then(() => console.log(`Date updated for report ${docid}`))
      .catch((error) => console.error("Error updating date:", error));
  };

  // Delete a report document by its document ID
  // Eerst bevestigen: één klik verwijderde het aanwezigheidsrapport meteen.
  const askDeleteReport = (docid) => {
    if (!docid) return;
    setReportToDelete(docid);
    setDeleteModal(true);
  };

  // Naar de prullenbak, niet definitief weg (ADR-008).
  // AI-CONTEXT: See docs/pitfalls.md#ui-soft-delete-promises-a-trash-it-is-not-in
  const deleteReport = async () => {
    if (!reportToDelete) return;
    await softDeleteDoc({
      collection: "PersonaPresenceReports",
      docid: reportToDelete,
      authId: auth?.ID,
      title: "Aanwezigheidsrapport",
    });
  };

  // Update the scheduled start time in the modal
  const saveUpdatedSchedule = () => {
    // turn "HH:MM" back into a number (e.g. 9)
    const parseHour = str => parseInt(str.split(':')[0], 10);
    db.collection("PersonaPresenceReports")
      .doc(updatedDocid)
      .update({
        Schedule: parseHour(updatedScheduleEnd) - parseHour(updatedScheduleStart)
      })
      .then(() => setOpenModal(false))
      .catch(err => console.error(err));
  };

  // Helper function to convert a "HH:MM" string to minutes since midnight
  const convertTimeToMinutes = (timeStr) => {
    if (typeof timeStr !== 'string' || !timeStr.includes(':')) {
      console.warn('Invalid time format:', timeStr);
      return 0; // or throw an error or return null, depending on your use case
    }
  
    const [hoursStr, minutesStr] = timeStr.split(':');
    const hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);
  
    if (isNaN(hours) || isNaN(minutes)) {
      console.warn('Invalid time numbers:', timeStr);
      return 0;
    }
  
    return hours * 60 + minutes;
  };
  

  // Update the attendance of a presence report document
    const saveUpdatedAttendance = () => {

        console.log(updatedDocid, updatedStart, updatedEnd);
        
        db.collection("PersonaPresenceReports")
            .doc(updatedDocid)
            .update({
              CorrectedStart: updatedStart,
              CorrectedEnd:   updatedEnd
            })            
            .then(() => {
                console.log(`Document ${updatedDocid} updated with new attendance values`);
                setOpenModal(false);
            })
            .catch((error) => {
                console.error(`Error updating document ${updatedDocid}:`, error);
            });
    }

    // Update the reason for absence of a presence report document
    const updateAbsentReasonHandler = (e) => {
        const newValue = e.target.value;
        const docid = e.target.dataset.docid;
    
        db.collection("PersonaPresenceReports")
          .doc(docid)
          .update({ 
            Reason: newValue 
        })
          .then(() => {
            console.log(`Document ${docid} updated with new absent reason: ${newValue}`);
          })
          .catch((error) => {
            console.error(`Error updating document ${docid}:`, error);
          }
        );
    };

  const tableColumns = [
    { key: 'drag', label: '', searchable: false },
    {
      key: 'date',
      label: 'DATUM',
      headerContent: (
        <div className='common-table__sort-header'>
          <span className='common-table__sort-header-title'>DATUM</span>
          {orderDates === 'asc' ? (
            <Tooltip content="Datum van nieuw naar oud" width="30px" top="20px">
              <KeyboardArrowUpOutlinedIcon onClick={() => setOrderDates('desc')} />
            </Tooltip>
          ) : (
            <Tooltip content="Datum van oud naar nieuw" width="30px" top="20px">
              <KeyboardArrowDownOutlinedIcon onClick={() => setOrderDates('asc')} />
            </Tooltip>
          )}
        </div>
      ),
    },
    { key: 'scheduled', label: 'GEROOSTERD (minuten)', searchable: false },
    { key: 'attendance', label: 'AANWEZIGHEID (minuten)', searchable: false },
    { key: 'difference', label: 'VERSCHIL (minuten)', searchable: false },
    { key: 'reason', label: 'REDEN', searchable: false },
    { key: 'delete', label: 'VERWIJDEREN', searchable: false },
  ];

  return (
    <div>
      <ReadOnlyGuard edit="personas.edit">
        {(canEdit) => (
          <Table
            columns={tableColumns}
            items={(() => {
            if (!visibleData || visibleData.length === 0) return dragArray || [];
            const visibleIds = new Set(visibleData.map(r => r.id));
            return (dragArray || []).filter(r => visibleIds.has(r.ID));
          })()}
            getItemKey={(presence) => presence.ID}
            renderRow={(presence) => (
              <>
                <td>
                  <DragIndicatorOutlinedIcon />
                </td>
                <td>
                  <input
                    type="date"
                    defaultValue={formatDateForInput(presence.Date)}
                    data-docid={presence.docid}
                    onBlur={updatePresenceDate}
                  />
                </td>
                <td>
                  <div id="presence-table-attendance-container">
                    <p>{(Number(presence.Schedule) || 0) * 60}</p>
                    {canEdit && (
                      <Tooltip content="Geroosterde uren aanpassen" width="30px" top="-70px">
                        <EditOutlinedIcon
                          onClick={() => {
                            setOpenModal(true);
                            setUpdatedDocid(presence.docid);
                            setUpdatedScheduleStart('09:00');
                            setUpdatedScheduleEnd('17:00');
                            setMOdalType('presence');
                          }}
                        />
                      </Tooltip>
                    )}
                  </div>
                </td>
                <td>
                  <div id="presence-table-attendance-container">
                    <p>
                      {getMinutesBetweenTimes(
                        presence.Start,
                        presence.End,
                        presence.CorrectedStart,
                        presence.CorrectedEnd,
                        presence.Type
                      )}
                    </p>
                    {canEdit && (
                      <Tooltip content="Aanwezigheid aanpassen" width="30px" top="-50px">
                        <EditOutlinedIcon
                          onClick={() => {
                            setOpenModal(true);
                            setUpdatedDocid(presence.docid);
                            setUpdatedStart(presence.CorrectedStart || presence.Start);
                            setUpdatedEnd(presence.CorrectedEnd || presence.End);
                            setMOdalType('attendance');
                          }}
                        />
                      </Tooltip>
                    )}
                  </div>
                </td>
                <td>
                  <p>
                    {getCorrectedMinutes(presence) - (presence.Schedule * 60)}
                  </p>
                </td>
                <td>
                  <input type="text" defaultValue={presence.Reason} data-docid={presence.docid} onChange={updateAbsentReasonHandler}/>
                </td>
                <td>
                  {canEdit && (
                    <Tooltip content="Verwijderen" width="30px" top="-40px">
                      <DeleteOutlinedIcon
                        className="table-delete-icon"
                        onClick={() => askDeleteReport(presence.docid)}
                        data-docid={presence.docid}
                      />
                    </Tooltip>
                  )}
                </td>
              </>
            )}
            searchAccessor={(presence) => `${presence.Date || ''} ${formatDateForInput(presence.Date)}`}
            searchPlaceholder="Zoeken op datum"
            emptyMessage="Geen aanwezigheidsrapporten gevonden."
            draggable={true}
            droppableId="presenceReports"
            onDragEnd={handleDragEnd}
            addRow={canEdit ? {
              toolTipContent: 'Aanwezigheidsrapport toevoegen',
              onClick: addReport,
            } : undefined}
          />
        )}
      </ReadOnlyGuard>
        <Modal
                isOpen={openModal}
                onRequestClose={() => setOpenModal(false)}
                style={modalStyles}
                contentLabel="Update attendance"
              >
                <ReadOnlyGuard edit="personas.edit"><div>
                  {modalType === 'attendance' ? (
                    <>
                      <div id="delete-modal-text-container">
                        <div id="delete-modal-title-container">
                          <h1>Aanwezigheid aanpassen</h1>
                        </div>
                        <div>
                          <h3>Pas de starttijd aan</h3>
                            <input type="time" defaultValue={updatedStart} onChange={(e) => setUpdatedStart(e.target.value)} />
                            <h3>Pas de eindtijd aan</h3>
                            <input type="time" defaultValue={updatedEnd} onChange={(e) => setUpdatedEnd(e.target.value)}/>
                        </div>
                      </div>
                      
                      <div id="delete-modal-button-container">
                        <button id="delete-modal-delete-button" onClick={() => setOpenModal(false)}>Annuleren</button>
                        <button onClick={saveUpdatedAttendance}>
                          Opslaan
                        </button>
                      </div>
                    </>
                  ) : (
                    <div>
                      <div id="delete-modal-text-container">
                        <h1>Geroosterde uren aanpassen</h1>
                        <label>
                          Starttijd
                          <input
                            type="time"
                            value={updatedScheduleStart}
                            onChange={e => setUpdatedScheduleStart(e.target.value)}
                          />
                        </label>
                        <label>
                          Eindtijd
                          <input
                            type="time"
                            value={updatedScheduleEnd}
                            onChange={e => setUpdatedScheduleEnd(e.target.value)}
                          />
                        </label>
                      </div>
                      
                      <div id="delete-modal-button-container">
                        <button id="delete-modal-delete-button" onClick={() => setOpenModal(false)}>Annuleren</button>
                        <button onClick={saveUpdatedSchedule}>Opslaan</button>
                      </div>
                    </div>
                  )}
                </div></ReadOnlyGuard>
              </Modal>
      <DeleteModal
        deleteModal={deleteModal}
        setDeleteModal={setDeleteModal}
        deleteItem={deleteReport}
        deleteName={'dit aanwezigheidsrapport'}
        softDelete
      />
    </div>
  );
};

export default PresenceTable;
